import { useMemo } from "react";
import type { DeployTaskRecord } from "@api/deploys.hooks";
import { logger } from "@lib/logger";
import { Deploy } from "@shared";

export type DeployScreen =
  | "in-progress"
  | "google-connect"
  | "invite-accept"
  | "payment-required"
  | "deploy-complete"
  | "deploy-error"
  | "deploy-cancelled"
  | "connection-error";

const TERMINAL_TASK_STATUSES = Deploy.TERMINAL_TASK_STATUSES as readonly string[];

/**
 * Pure function that resolves which content screen to show.
 *
 * Logic:
 * 1. Find the first non-terminal task
 * 2. Map its name to a screen
 * 3. If all tasks are terminal → check deploy status for complete/error
 * 4. If polling failed → connection-error
 */
export function resolveContentScreen(
  tasks: DeployTaskRecord[] | undefined,
  deployStatus: string | undefined,
  pollingFailed?: boolean
): DeployScreen {
  // Connection error — polling exhausted retries
  if (pollingFailed && (!tasks || tasks.length === 0)) {
    logger.debug("DeployScreen", "resolved: connection-error");
    return "connection-error";
  }

  if (deployStatus === "failed") return "deploy-error";
  if (deployStatus === "cancelled") return "deploy-cancelled";
  if (deployStatus === "completed") return "deploy-complete";

  if (!tasks || tasks.length === 0) {
    return "in-progress";
  }

  // Find first non-terminal task
  const activeTask = tasks.find((t) => !TERMINAL_TASK_STATUSES.includes(t.status));
  if (!activeTask) {
    return "deploy-error"; // This should never get hit, but show a fixable fallback
  }

  // Map task name to screen
  switch (activeTask.name) {
    case "ConnectingGoogle":
      return "google-connect";
    case "VerifyingGoogle":
      return "invite-accept";
    case "CheckingBilling":
      return "payment-required";
    default:
      return "in-progress";
  }
}

/**
 * Hook that returns the current content screen based on deploy tasks and status.
 */
export function useDeployContentScreen(
  tasks: DeployTaskRecord[] | undefined,
  deployStatus: string | undefined,
  pollingFailed?: boolean
): DeployScreen {
  return useMemo(
    () => resolveContentScreen(tasks, deployStatus, pollingFailed),
    [tasks, deployStatus, pollingFailed]
  );
}
